export const TEAM_NAMES = ["Mike", "Chris", "Sarah", "Kevin", "Rachel", "Tony", "Dana", "Marcus", "Jenny", "Steve", "Alex", "Luke", "Jake", "Evan", "Cole", "Drew"];

// D2 (chunk 5 part 2, shipped chunk 9) — the raw rb$/wr$ pairs below carry a
// 10% RB-share shift ALREADY FOLDED IN: rb$ = rb0 + 0.10*wr0, wr$ = 0.90*wr0.
// These are the EXACT-VALUE literals, not the rounded integer edit part 3
// described: every one is Object.is-identical to what `_demandDials.rbShift =
// 0.10` computes from the pre-shift pair, for all seven archetypes. The integer
// edit was rejected for ship because rounding differs on four of the seven
// (WR Heavy 59.8, Balanced 71.5, Stars & Scrubs 38.5, The Hammer 68.2 are not
// integers) and it cannot reproduce the accepted cell's digest.
//
// Pre-shift pairs, for reference and for reversing the fold:
//   RB Heavy 100/60 · WR Heavy 50/98 · Balanced 65/65 · Stars & Scrubs 35/35
//   Zero RB 22/90 · Hero RB 55/80 · The Hammer 62/62
//
// Every raw sum (qb$+rb$+wr$+te$) is preserved by the transfer, so the
// renormalization scale factor at simulation.js:296 is unchanged and qb$/te$
// are untouched. `_demandDials.rbShift` stays at 0 by default and remains
// probe-reachable — setting it now shifts a SECOND time, on top of this fold.
export const ARCHETYPES = {
  "RB Heavy": {
    qb$: 12, rb$: 106.0, wr$: 54.0, te$: 10, selfNomRate: 0.50, overpay: 4, poisonRate: 0.65,
    style: "3 startable RBs, FLEX = RB", flexPos: "RB", starterOverrides: { RB: 3 },
    shoppingList: {
      RB1:  { tiers: [1],       maxBid: [0.225, 0.325] },
      RB2:  { tiers: [2, 3],    maxBid: [0.15, 0.25] },
      FLEX: { tiers: [4, 5],    maxBid: [0.075, 0.15], pos: "RB" },
      WR1:  { tiers: [2, 3],    maxBid: [0.125, 0.20] },
      WR2:  { tiers: [3, 4],    maxBid: [0.05, 0.125] },
      WR3:  { tiers: [5, 6, 7], maxBid: [0.005, 0.05] },
      QB1:  { tiers: [3, 4, 5], maxBid: [0.005, 0.075] },
      TE1:  { tiers: [3, 4, 5], maxBid: [0.005, 0.075] },
    },
    posWillingness: { RB: 1.15, WR: 0.90, QB: 0.60, TE: 0.80 },
  },
  "WR Heavy": {
    qb$: 12, rb$: 59.8, wr$: 88.2, te$: 6, selfNomRate: 0.50, overpay: 3, poisonRate: 0.60,
    // D1 (chunk 5 part 2, shipped chunk 9): rbSpendCap 60 -> 75. NOTE it is read
    // raw at auction.js:857 and never multiplied by budgetScale, so at $200 this
    // is 37.5% of budget but only 31% in a $240 league. Same family as the
    // Balanced $40 clamp; the non-scaling is a separate open item (backlog).
    style: "Elite WRs, FLEX = WR", flexPos: "WR", starterOverrides: { WR: 4 }, rbSpendCap: 75, flexReserve: 15,
    shoppingList: {
      WR1:  { tiers: [1],       maxBid: [0.225, 0.325] },
      WR2:  { tiers: [2],       maxBid: [0.15, 0.225] },
      WR3:  { tiers: [3],       maxBid: [0.075, 0.15] },
      FLEX: { tiers: [4, 5],    maxBid: [0.04, 0.10], pos: "WR" },
      RB1:  { tiers: [2, 3],    maxBid: [0.15, 0.225] },
      RB2:  { tiers: [6, 7],    maxBid: [0.005, 0.05] },
      QB1:  { tiers: [3, 4, 5], maxBid: [0.005, 0.075] },
      TE1:  { tiers: [5, 6],    maxBid: [0.005, 0.015] },
    },
    posWillingness: { RB: 0.90, WR: 1.15, QB: 0.60, TE: 0.60 },
  },
  "Balanced": {
    qb$: 20, rb$: 71.5, wr$: 58.5, te$: 13, selfNomRate: 0.50, overpay: 2, poisonRate: 0.55,
    style: "Even spread, FLEX = BPA",
    shoppingList: {
      RB1:  { tiers: [2, 3],    maxBid: [0.15, 0.20] },
      RB2:  { tiers: [4, 5],    maxBid: [0.075, 0.14] },
      WR1:  { tiers: [2, 3],    maxBid: [0.125, 0.175] },
      WR2:  { tiers: [3, 4],    maxBid: [0.075, 0.125] },
      WR3:  { tiers: [4, 5],    maxBid: [0.025, 0.075] },
      QB1:  { tiers: [1, 2],    maxBid: [0.10, 0.20] },
      TE1:  { tiers: [2, 3, 4], maxBid: [0.04, 0.10] },
      FLEX: { tiers: [4, 5, 6], maxBid: [0.025, 0.075] },
    },
    posWillingness: { RB: 1.00, WR: 1.00, QB: 1.00, TE: 1.00 },
  },
  "Stars & Scrubs": {
    qb$: 8, rb$: 38.5, wr$: 31.5, te$: 5, selfNomRate: 0.45, overpay: 0, poisonRate: 0.50,
    style: "3 stars then $1-3 fills", starsMode: true,
    shoppingList: {
      STAR1: { tiers: [1],       maxBid: [0.225, 0.325], pos: "ANY" },
      STAR2: { tiers: [1],       maxBid: [0.225, 0.325], pos: "ANY" },
      STAR3: { tiers: [1, 2],    maxBid: [0.175, 0.275], pos: "ANY" },
      RB1:   { tiers: [7, 8, 9],    maxBid: [0.005, 0.015] },
      RB2:   { tiers: [7, 8, 9],    maxBid: [0.005, 0.015] },
      WR1:   { tiers: [7, 8, 9],    maxBid: [0.005, 0.015] },
      WR2:   { tiers: [7, 8, 9],    maxBid: [0.005, 0.015] },
      WR3:   { tiers: [7, 8, 9],    maxBid: [0.005, 0.015] },
      QB1:   { tiers: [5, 6, 7],    maxBid: [0.005, 0.015] },
      TE1:   { tiers: [5, 6],       maxBid: [0.005, 0.015] },
    },
    posWillingness: { RB: 1.50, WR: 1.50, QB: 0.15, TE: 0.15 },
  },
  "Zero RB": {
    qb$: 18, rb$: 31.0, wr$: 81.0, te$: 14, selfNomRate: 0.50, overpay: 2, poisonRate: 0.55,
    style: "Punt RB, elite WRs", starterOverrides: { WR: 3 }, rbCap: 10,
    shoppingList: {
      WR1:  { tiers: [1],       maxBid: [0.225, 0.30] },
      WR2:  { tiers: [2],       maxBid: [0.125, 0.20] },
      WR3:  { tiers: [2, 3],    maxBid: [0.075, 0.15] },
      WR4:  { tiers: [4, 5],    maxBid: [0.05, 0.125] },
      WR5:  { tiers: [5, 6],    maxBid: [0.025, 0.075] },
      FLEX: { tiers: [5, 6],    maxBid: [0.025, 0.06], pos: "RB" },
      RB1:  { tiers: [7, 8],    maxBid: [0.005, 0.04] },
      RB2:  { tiers: [7, 8, 9], maxBid: [0.005, 0.025] },
      QB1:  { tiers: [1, 2],    maxBid: [0.10, 0.225] },
      TE1:  { tiers: [3, 4],    maxBid: [0.025, 0.075] },
    },
    posWillingness: { RB: 0.40, WR: 1.15, QB: 1.05, TE: 0.90 },
  },
  "Hero RB": {
    qb$: 18, rb$: 63.0, wr$: 72.0, te$: 10, selfNomRate: 0.55, overpay: 3, poisonRate: 0.55,
    style: "1 elite RB then punt RB", starterOverrides: { WR: 3 }, heroRB: true,
    shoppingList: {
      RB1:  { tiers: [1],       maxBid: [0.225, 0.30] },
      RB2:  { tiers: [7, 8],    maxBid: [0.005, 0.025] },
      WR1:  { tiers: [1, 2],    maxBid: [0.175, 0.275] },
      WR2:  { tiers: [3],       maxBid: [0.075, 0.125] },
      WR3:  { tiers: [4, 5],    maxBid: [0.025, 0.075] },
      FLEX: { tiers: [5, 6],    maxBid: [0.015, 0.05], pos: "WR" },
      QB1:  { tiers: [3, 4],    maxBid: [0.025, 0.075] },
      TE1:  { tiers: [4, 5],    maxBid: [0.005, 0.05] },
    },
    posWillingness: { RB: 1.40, WR: 0.35, QB: 0.35, TE: 0.35 },
  },
  "The Hammer": {
    qb$: 15, rb$: 68.2, wr$: 55.8, te$: 10, selfNomRate: 0.40, overpay: 1, poisonRate: 0.70,
    style: "Sits back early, dominates mid-draft with budget advantage", hammerMode: true,
    shoppingList: {
      RB1:  { tiers: [1, 2, 3, 4], maxBid: [0.15, 0.25] },
      RB2:  { tiers: [5, 6],    maxBid: [0.05, 0.11] },
      WR1:  { tiers: [1, 2, 3], maxBid: [0.12, 0.20] },
      WR2:  { tiers: [3, 4],    maxBid: [0.06, 0.11] },
      WR3:  { tiers: [5, 6],    maxBid: [0.015, 0.075] },
      FLEX: { tiers: [5, 6, 7], maxBid: [0.015, 0.06] },
      QB1:  { tiers: [3, 4],    maxBid: [0.025, 0.075] },
      TE1:  { tiers: [4, 5],    maxBid: [0.005, 0.05] },
    },
    posWillingness: { RB: 1.00, WR: 1.00, QB: 1.00, TE: 1.00 },
  },
};

export const MODIFIERS = {
  "Elite QB":    { overrides: { qb$: 40 }, conflicts: ["Punt QB", "Balanced QB", "Elite QB SF", "Balanced QB SF", "Punt QB SF"],  desc: "QB budget $25-55", shoppingListOverrides: { QB1: { tiers: [1, 2], maxBid: [0.15, 0.275] } } },
  "Punt QB":     { overrides: { qb$: 6 },  conflicts: ["Elite QB", "Balanced QB", "Elite QB SF", "Balanced QB SF", "Punt QB SF"], desc: "QB budget $1-12",  shoppingListOverrides: { QB1: { tiers: [4, 5, 6], maxBid: [0.005, 0.06] } } },
  "Balanced QB": { overrides: {},          conflicts: ["Elite QB", "Punt QB", "Elite QB SF", "Balanced QB SF", "Punt QB SF"],     desc: "QB budget default", shoppingListOverrides: {} },
  "Elite TE":    { overrides: { te$: 35 }, conflicts: ["Punt TE", "Balanced TE"],  desc: "TE budget $30-45", shoppingListOverrides: { TE1: { tiers: [1, 2], maxBid: [0.10, 0.175] } } },
  "Punt TE":     { overrides: { te$: 2 },  conflicts: ["Elite TE", "Balanced TE"], desc: "TE budget $1-4",   shoppingListOverrides: { TE1: { tiers: [5, 6], maxBid: [0.005, 0.02] } } },
  "Balanced TE": { overrides: {},          conflicts: ["Elite TE", "Punt TE"],      desc: "TE budget default", shoppingListOverrides: {} },
  "Enforcer": { overrides: {},          conflicts: [],            desc: "Bids up undervalued players", shoppingListOverrides: {} },
  "Homer":    { overrides: {},          conflicts: [],            desc: "Overpays for fav NFL team",   shoppingListOverrides: {} },
  "Patient Spender": { overrides: {}, conflicts: ["The Hammer", "Homer"], desc: "Sits back early, big war chest mid-draft", shoppingListOverrides: {} },
  // SFLEX QB strategy modifiers — control QB spending tier in superflex formats
  "Elite QB SF":    { overrides: { qb$: 70 }, conflicts: ["Balanced QB SF", "Punt QB SF", "Elite QB", "Punt QB", "Balanced QB"], desc: "SFLEX: two expensive QBs (28-33% QB spend)" },
  "Balanced QB SF": { overrides: { qb$: 48 }, conflicts: ["Elite QB SF", "Punt QB SF", "Elite QB", "Punt QB", "Balanced QB"],   desc: "SFLEX: two solid QBs (16-24% QB spend)" },
  "Punt QB SF":     { overrides: { qb$: 30 }, conflicts: ["Elite QB SF", "Balanced QB SF", "Elite QB", "Punt QB", "Balanced QB"],  desc: "SFLEX: two cheap QBs (10-14% QB spend)" },
};

export const DEFAULT_AI_ARCHETYPES = ["RB Heavy", "Balanced", "WR Heavy", "RB Heavy", "Balanced", "WR Heavy", "Stars & Scrubs", "Balanced", "WR Heavy", "The Hammer", "Hero RB", "Zero RB", "RB Heavy", "Balanced", "WR Heavy", "Stars & Scrubs"];

export const DEFAULT_AI_MODIFIERS = [
  ["Punt QB","Enforcer"],["Elite QB","Elite TE"],[],["Punt QB"],["Elite QB"],["Punt QB"],["Punt TE"],["Enforcer"],["Punt QB","Punt TE"],["Elite QB"],
  ["Homer"],["Enforcer"],["Punt TE"],["Punt QB"],[],["Homer"]
];

export const MOCK_TEAM_NAMES = TEAM_NAMES;
export const MOCK_ROSTER_SIZE = 16;

export const SPEED_SETTINGS = {
  slow:    { label: "Subsonic",   aiNomSec: [3, 5], aiBidMs: [2000, 5000], userNomSec: 60, phaseSec: 3,   soldDelayMs: 2000 },
  normal:  { label: "Cruising",   aiNomSec: [2, 4], aiBidMs: [1000, 4000], userNomSec: 45, phaseSec: 2,   soldDelayMs: 1500 },
  fast:    { label: "Supersonic", aiNomSec: [1, 2], aiBidMs: [300, 1000],  userNomSec: 30, phaseSec: 1.5, soldDelayMs: 1000 },
  instant: { label: "Mach 1",    aiNomSec: [0, 0], aiBidMs: [0, 0],      userNomSec: 15, phaseSec: 1,   soldDelayMs: 500  },
};
